import { useState, useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import { getProcessedEvidenceByFileId } from "../../../../../apis/settingsApis/processed-evidence/processedApis";
import BlinkingStatusDot from "../../../../../components/BlinkingStatusDot/BlinkingStatusDot";
import {
  renderValidationStatusChip,
  renderCheckNumbers,
  renderCheckDetails,
} from "../Control-Components/ControlComponents";
import { EvidenceStatus, CheckStatus } from "../../../../../utils/enums";
const ITEMS_PER_PAGE = 5;
export const useEvidenceManagement = (control, fileDetails) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedEvidenceId, setExpandedEvidenceId] = useState(null);
  const [evidenceChecksData, setEvidenceChecksData] = useState({});
  const [evidenceChecksLoading, setEvidenceChecksLoading] = useState({});
  const client = useApolloClient();

  const checkNumberMap = useMemo(() => {
    const map = {};
    if (control?.checks) {
      control.checks.forEach((check, index) => {
        const checkId = check.activeCheckId || check.checkId || check.id;
        if (checkId) {
          map[checkId] = index + 1;
          map[String(checkId)] = index + 1;
        }
      });
    }
    return map;
  }, [control?.checks]);

  const getEvidenceList = () => {
    return fileDetails.map((fileDetail) => ({
      ...fileDetail,
      associatedChecks: evidenceChecksData[fileDetail.fileId] || [],
    }));
  };

  const getCheckStatusFromControl = (checkId) => {
    if (!control?.checks) return CheckStatus.PENDING;

    const checkIdNum = Number(checkId);
    const checkIdStr = String(checkId);

    const controlCheck = control.checks.find((check) => {
      const controlCheckId = check.activeCheckId || check.checkId || check.id;
      return (
        controlCheckId === checkId ||
        controlCheckId === checkIdNum ||
        controlCheckId === checkIdStr ||
        String(controlCheckId) === checkIdStr ||
        Number(controlCheckId) === checkIdNum
      );
    });

    return controlCheck?.checkStatus || CheckStatus.PENDING;
  };

  const getFileValidationStatus = (fileDetail) => {
    const isFileValid = fileDetail.isFileValid;
    const associatedChecks = fileDetail.associatedChecks || [];

    if (isFileValid === true) {
      return {
        status: EvidenceStatus?.VALID,
        color: "green",
        dotColor: "#4CAF50",
        chipStatus: EvidenceStatus?.VALID,
        message:
          associatedChecks.length > 0
            ? associatedChecks.length === 1
              ? `1 Check Linked`
              : `${associatedChecks.length} Checks Linked`
            : "Acceptable",
        showChecks: true,
      };
    } else if (isFileValid === null) {
      return {
        status: EvidenceStatus?.PROCESSING,
        color: "orange",
        dotColor: "#FF8800",
        chipStatus: EvidenceStatus?.PROCESSING,
        message: "Processing...",
        showChecks: false,
      };
    } else {
      return {
        status: EvidenceStatus?.INVALID,
        color: "red",
        dotColor: "#f44336",
        chipStatus: EvidenceStatus?.INVALID,
        message: "Invalid",
        showChecks: false,
      };
    }
  };

  const evidenceList = getEvidenceList();
  const totalItems = evidenceList.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const paginatedData = evidenceList.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    setExpandedEvidenceId(null);
  };

  const toggleExpand = async (fileId) => {
    if (expandedEvidenceId === fileId) {
      setExpandedEvidenceId(null);
      return;
    }
    setExpandedEvidenceId(fileId);

    // If already loaded, don't fetch again
    if (evidenceChecksData[fileId] || evidenceChecksLoading[fileId]) return;

    setEvidenceChecksLoading((prev) => ({ ...prev, [fileId]: true }));

    try {
      const response = await client.query({
        query: getProcessedEvidenceByFileId,
        variables: { file_id: fileId },
        fetchPolicy: "network-only",
      });
      const evidenceData = response.data?.getProcessedEvidenceByFileId;
      let checks = [];
      if (Array.isArray(evidenceData)) {
        checks = evidenceData.flatMap((item) => item.checks || []);
      } else if (evidenceData) {
        checks = evidenceData.checks || [];
      }
      setEvidenceChecksData((prev) => ({ ...prev, [fileId]: checks }));
    } catch (error) {
      setEvidenceChecksData((prev) => ({ ...prev, [fileId]: [] }));
    }
    setEvidenceChecksLoading((prev) => ({ ...prev, [fileId]: false }));
  };

  return {
    currentPage,
    expandedEvidenceId,
    evidenceChecksData,
    evidenceChecksLoading,
    checkNumberMap,
    evidenceList,
    paginatedData,
    totalItems,
    totalPages,
    handlePageChange,
    toggleExpand,
    getFileValidationStatus,
    getCheckStatusFromControl,
    renderValidationStatusChip: (validationStatus) =>
      renderValidationStatusChip(validationStatus),
    renderStatusDot: (validationStatus) => BlinkingStatusDot(validationStatus),
    renderCheckNumbers: (associatedChecks, validationStatus) =>
      renderCheckNumbers(associatedChecks, validationStatus, checkNumberMap),
    renderCheckDetails: (associatedChecks, validationStatus) =>
      renderCheckDetails(
        associatedChecks,
        validationStatus,
        checkNumberMap,
        getCheckStatusFromControl
      ),
  };
};
